import type { PageParam, PageResult, PagedMetaEnvelope, PagedQueryArg } from './paged-types';
import { buildPagedQueryString } from './query-params';

export type WorkspaceListItem = {
    id: string;
    name: string;
    description: string;
    access: 'private' | 'public';
    state: string;
    tenantId: string;
    containerId: string;
    workspaceTierId: string;
    imageStore: string;
    clientStore: string;
    createdAt: string;
    updatedAt: string;
};

type WorkspaceApiItem = {
    id?: string;
    name?: string | null;
    description?: string | null;
    access?: WorkspaceListItem['access'] | null;
    state?: string | null;
    tenantId?: string | null;
    containerId?: string | null;
    workspaceTierId?: string | null;
    imageStore?: string | null;
    clientStore?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
};

type WorkspacesApiResponse = {
    workspaces?: WorkspaceApiItem[];
    meta?: PagedMetaEnvelope;
};

type WorkspaceApiItemWithId = WorkspaceApiItem & { id: string };

const hasId = (workspace: WorkspaceApiItem): workspace is WorkspaceApiItemWithId =>
    typeof workspace.id === 'string';

const transformWorkspace = (workspace: WorkspaceApiItemWithId): WorkspaceListItem => ({
    id: workspace.id,
    name: workspace.name ?? '',
    description: workspace.description ?? '',
    access: workspace.access ?? 'private',
    state: workspace.state ?? 'I',
    tenantId: workspace.tenantId ?? '',
    containerId: workspace.containerId ?? '',
    workspaceTierId: workspace.workspaceTierId ?? '',
    imageStore: workspace.imageStore ?? '',
    clientStore: workspace.clientStore ?? '',
    createdAt: workspace.createdAt ?? '',
    updatedAt: workspace.updatedAt ?? '',
});

export type WorkspacesPageResult = PageResult<WorkspaceListItem>;
export type WorkspacesQueryArg = PagedQueryArg;

export const workspacesInfiniteQuery = {
    query: ({ queryArg, pageParam }: { queryArg: WorkspacesQueryArg; pageParam: PageParam }) => ({
        url: `/mp/1/workspaces?${buildPagedQueryString({
            offset: pageParam.offset,
            limit: queryArg.limit,
            includeCount: true,
            order: queryArg.order,
            where: queryArg.where,
            extraParams: queryArg.extraParams,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: WorkspacesApiResponse): WorkspacesPageResult => {
        const rows = (response?.workspaces ?? []).filter(hasId).map(transformWorkspace);
        return {
            rows,
            total: response?.meta?.total ?? rows.length,
            offset: response?.meta?.offset ?? 0,
            limit: response?.meta?.limit ?? rows.length,
        };
    },
    keepUnusedDataFor: 0,
    infiniteQueryOptions: {
        initialPageParam: { offset: 0 },
        getNextPageParam: (
            lastPage: WorkspacesPageResult,
            _allPages: WorkspacesPageResult[],
            lastPageParam: PageParam,
        ): PageParam | undefined => {
            if (lastPage.rows.length === 0) return undefined;
            const loaded = lastPageParam.offset + lastPage.rows.length;
            return loaded < lastPage.total ? { offset: loaded } : undefined;
        },
        getPreviousPageParam: (): PageParam | undefined => undefined,
    },
};
